import { useState } from 'react';

import PropTypes from 'prop-types';
import { BsArrowRepeat, BsTrash } from 'react-icons/bs';

import Alert from '#globalComponents/Alert/Alert';
import Button from '#globalComponents/Button/Button';
import Card from '#globalComponents/Card/Card';
import IconButton from '#globalComponents/IconButton/IconButton';
import Tag from '#globalComponents/Tag/Tag';
import { useError } from '#globalContexts/ErrorContext';
import { formatDate } from '#lib/dateUtils';
import { useResendInvite, useRevokeInvite } from '#orgFeature/models/org/mutations';

const ROLE_LABELS = {
	admin: 'Admin',
	member: 'Mitglied',
};

const RevokeConfirmation = ({ invite, onCancel, onConfirm, isRevoking }) => (
	<Alert tone="warning" title={`Einladung an ${invite.email} zurückziehen?`} className="mt-3">
		<p>Die Einladung wird gelöscht. Gehört das Konto zu keiner anderen Organisation, wird es ebenfalls gelöscht.</p>
		<div className="mt-3 flex justify-end gap-2">
			<Button variant="outline" size="sm" onClick={onCancel}>
				Abbrechen
			</Button>
			<Button variant="danger" size="sm" onClick={onConfirm} loading={isRevoking}>
				Zurückziehen
			</Button>
		</div>
	</Alert>
);

RevokeConfirmation.propTypes = {
	invite: PropTypes.object.isRequired,
	onCancel: PropTypes.func.isRequired,
	onConfirm: PropTypes.func.isRequired,
	isRevoking: PropTypes.bool.isRequired,
};

const InviteRow = ({ invite, isConfirmingRevoke, isResending, isRevoking, onToggleRevoke, onResend, onRevoke }) => (
	<div className="px-4 py-3">
		<div className="flex flex-wrap items-center justify-between gap-3">
			<div className="min-w-0 flex-1">
				<p className="truncate text-sm font-medium text-text">{invite.email}</p>
				<p className="text-xs text-bgBase-800">
					Eingeladen am {formatDate(invite.invitedAt)}
					{!invite.expired && <> &middot; gültig bis {formatDate(invite.expiresAt)}</>}
				</p>
			</div>

			<div className="flex flex-shrink-0 items-center gap-3">
				{invite.expired && <Tag label="Abgelaufen" tone="warning" />}
				<Tag label={ROLE_LABELS[invite.role] || invite.role} tone="neutral" />
				<div className="flex gap-1">
					<IconButton
						icon={BsArrowRepeat}
						label="Einladung erneut senden"
						tone="primary"
						disabled={isResending}
						onClick={() => onResend(invite)}
					/>
					<IconButton
						icon={BsTrash}
						label="Einladung zurückziehen"
						tone="danger"
						onClick={() => onToggleRevoke(invite.id)}
					/>
				</div>
			</div>
		</div>

		{isConfirmingRevoke && (
			<RevokeConfirmation
				invite={invite}
				onCancel={() => onToggleRevoke(null)}
				onConfirm={() => onRevoke(invite)}
				isRevoking={isRevoking}
			/>
		)}
	</div>
);

InviteRow.propTypes = {
	invite: PropTypes.object.isRequired,
	isConfirmingRevoke: PropTypes.bool.isRequired,
	isResending: PropTypes.bool.isRequired,
	isRevoking: PropTypes.bool.isRequired,
	onToggleRevoke: PropTypes.func.isRequired,
	onResend: PropTypes.func.isRequired,
	onRevoke: PropTypes.func.isRequired,
};

export const PendingInviteList = ({ invites }) => {
	const [confirmRevokeId, setConfirmRevokeId] = useState(null);
	const { showSuccess, showError } = useError();
	const resendMutation = useResendInvite();
	const revokeMutation = useRevokeInvite();

	if (invites.length === 0) return null;

	const handleResend = (invite) => {
		resendMutation.mutate(invite.id, {
			onSuccess: ({ expiresAt }) =>
				showSuccess({
					title: 'Einladung erneut gesendet',
					message: `An ${invite.email}, gültig bis ${formatDate(expiresAt)}.`,
					autoClose: true,
				}),
			onError: (error) => showError({ error, title: 'Einladung konnte nicht gesendet werden' }),
		});
	};

	const handleRevoke = (invite) => {
		revokeMutation.mutate(invite.id, {
			onSuccess: ({ accountDeleted }) => {
				setConfirmRevokeId(null);
				showSuccess({
					title: 'Einladung zurückgezogen',
					message: accountDeleted
						? `Die Einladung an ${invite.email} und das zugehörige Konto wurden gelöscht.`
						: `Die Einladung an ${invite.email} wurde gelöscht.`,
					autoClose: true,
				});
			},
			onError: (error) => showError({ error, title: 'Einladung konnte nicht zurückgezogen werden' }),
		});
	};

	return (
		<Card className="overflow-hidden p-0">
			<div className="border-b border-bgBase-500 px-4 py-3">
				<h3 className="text-sm font-semibold text-text">Ausstehende Einladungen</h3>
			</div>

			<div className="divide-y divide-bgBase-500">
				{invites.map((invite) => (
					<InviteRow
						key={invite.id}
						invite={invite}
						isConfirmingRevoke={confirmRevokeId === invite.id}
						isResending={resendMutation.isPending && resendMutation.variables === invite.id}
						isRevoking={revokeMutation.isPending && revokeMutation.variables === invite.id}
						onToggleRevoke={(id) => setConfirmRevokeId(confirmRevokeId === id ? null : id)}
						onResend={handleResend}
						onRevoke={handleRevoke}
					/>
				))}
			</div>
		</Card>
	);
};

PendingInviteList.propTypes = {
	invites: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.string.isRequired,
			email: PropTypes.string,
			role: PropTypes.string.isRequired,
			invitedAt: PropTypes.string.isRequired,
			expiresAt: PropTypes.string.isRequired,
			expired: PropTypes.bool.isRequired,
		}),
	).isRequired,
};
